import { useEffect, useState } from "react";
import { DealerDogBrand } from "@/components/layout/dealerdog-brand";
import { Link, useLocation } from "react-router-dom";
import "@/styles/dd-public.css";

export { CALENDLY_URL } from "@/lib/site-links";

type NavItem = { href: string; label: string };

const HOME_NAV: NavItem[] = [
  { href: "#platform", label: "Platform" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#benefits", label: "Benefits" },
  { href: "#industries", label: "Industries" },
  { href: "#faq", label: "FAQs" },
  { href: "/articles", label: "Insights" },
  { href: "/tradeshows", label: "Events" },
  { href: "#contact", label: "Contact" },
];

const MARKETING_NAV: NavItem[] = [
  { href: "/articles", label: "Insights" },
  { href: "/tradeshows", label: "Events" },
];

type PublicSiteHeaderProps = {
  variant?: "home" | "marketing";
  onCreateAccount?: () => void;
  scrollSpySectionIds?: string[];
};

function MarketingNavLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
  return (
    <Link to={item.href} className={active ? "active" : undefined} onClick={onNavigate}>
      {item.label}
    </Link>
  );
}

/** Hash section links must target home when this header is used off `/`. */
function homeHashHref(pathname: string, href: string) {
  if (!href.startsWith("#")) return href;
  return pathname === "/" ? href : `/${href}`;
}

export function PublicSiteHeader({
  variant = "marketing",
  onCreateAccount,
  scrollSpySectionIds,
}: PublicSiteHeaderProps) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState("");
  const navItems = variant === "home" ? HOME_NAV : MARKETING_NAV;

  useEffect(() => {
    document.body.classList.toggle("dd-menu-open", menuOpen);
    return () => document.body.classList.remove("dd-menu-open");
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (variant !== "home" || !scrollSpySectionIds?.length) return;

    const sections = scrollSpySectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setActiveHash(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-25% 0px -64% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [variant, scrollSpySectionIds]);

  const closeMenu = () => setMenuOpen(false);

  const createAccountControl = onCreateAccount ? (
    <button type="button" className="nav-cta" onClick={onCreateAccount}>
      Create an Account
    </button>
  ) : (
    <Link className="nav-cta" to="/#create-account">
      Create an Account
    </Link>
  );

  const mobileCreateAccount = onCreateAccount ? (
    <button
      type="button"
      onClick={() => {
        closeMenu();
        onCreateAccount();
      }}
    >
      Create Account
    </button>
  ) : (
    <Link to="/#create-account" onClick={closeMenu}>
      Create Account
    </Link>
  );

  return (
    <>
      <a className="dd-skip-link" href="#main-content">
        Skip to content
      </a>

      <header className={["dd-site-header", scrolled ? "scrolled" : ""].filter(Boolean).join(" ")}>
        <div className="dd-container nav-wrap">
          <Link className="brand" to="/" aria-label="DealerDog home">
            <DealerDogBrand />
          </Link>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map((item) => {
              const isHash = item.href.startsWith("#");
              if (isHash) {
                return (
                  <a
                    key={item.href}
                    href={homeHashHref(pathname, item.href)}
                    className={activeHash === item.href ? "active" : undefined}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </a>
                );
              }
              return <MarketingNavLink key={item.href} item={item} />;
            })}
          </nav>

          <div className="nav-actions">
            <Link className="nav-login" to="/login">
              Log In
            </Link>
            {createAccountControl}
          </div>

          <button
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-panel"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className={["mobile-panel", menuOpen ? "open" : ""].filter(Boolean).join(" ")} id="mobile-panel">
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {navItems.map((item) => {
            const isHash = item.href.startsWith("#");
            if (isHash) {
              return (
                <a key={item.href} href={homeHashHref(pathname, item.href)} onClick={closeMenu}>
                  {item.label} <span>→</span>
                </a>
              );
            }
            return (
              <Link key={item.href} to={item.href} onClick={closeMenu}>
                {item.label} <span>→</span>
              </Link>
            );
          })}
          <div className="mobile-nav-actions">
            <Link to="/login" onClick={closeMenu}>
              Log In
            </Link>
            {mobileCreateAccount}
          </div>
        </nav>
      </div>
    </>
  );
}
